import { ref, computed } from 'vue';
import { of } from 'rxjs';
import { filter, tap, switchMap } from 'rxjs/operators';
import { useRoute } from 'vue-router';
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { useAccountStore } from 'src/stores/account';
import { useCentralStore } from 'src/stores/central';
import { useLoggingStore } from 'src/stores/logging';
import { useSearchStore } from 'src/stores/search';
import { useViewStore } from 'src/stores/view';
import { rxInitSubjectCallback, useObservable } from 'src/plugins/rxjs/base';
import { RefreshPagePlugin } from 'src/plugins/general/refreshPage';
import { AppGeneralVariables } from 'src/plugins/general/variables';
import { axiosCallerPlugin } from 'src/plugins/axios/componentAxiosCallerPlugin';
/**
 * Meta update for mode switching
 */
const LayoutPlugin = () => {
    const { styleType, appMounted } = AppGeneralVariables();
    const accountStore = useAccountStore();
    const centralStore = useCentralStore();
    const loggingStore = useLoggingStore();
    const searchStore = useSearchStore();
    const viewStore = useViewStore();
    const vueRoute = useRoute();
    const $q = useQuasar();
    const user = computed(() => accountStore.user);
    const showHeader = computed(() => {
        return (styleType.value !== 'desktop') || !((typeof vueRoute.name === 'string') &&
            (['GetSpaces', 'Generic Landing Pages', 'Home', 'Reset Password'].indexOf(vueRoute.name) > -1));
    });
    const pagePaddingClass = computed(() => {
        if (!appMounted.value && showHeader.value) {
            return {
                'layout-padding-desktop': styleType.value === 'desktop' && user.value,
                'layout-padding-mobile': (styleType.value === 'mobile' || (styleType.value === 'desktop' && !user.value)),
                'layout-padding-app': styleType.value === 'app'
            };
        }
        return {};
    });
    /****
     * Subscription component
     *****/
    const subscriptionSticky = ref(null);
    function updateSubscriber(isVisible) {
        if (subscriptionSticky.value !== null) {
            // @ts-expect-error I dont know why got ts error here
            subscriptionSticky.value?.updateSubscriber(!isVisible);
        }
    }
    const drawer = computed({
        get: () => viewStore.drawer,
        set: val => {
            viewStore.toggleDrawer(val);
            if (!val) {
                viewStore.toggleDrawerTab('functions');
                return;
            }
            $q.sessionStorage.set('openingChat', true);
        }
    });
    /**
     * Standard behaviours when page is clicked
     * @param evt click event
     */
    function checkPageClick(evt) {
        /****
         * Close filter if click is outside of filter
         ****/
        if (([
            'filters', 'filter_alt', 'filter_altfilters', 'filtersfilter_alt', 'Filters'
        ].indexOf(evt.target.textContent?.toLowerCase() || '') === -1) && (evt.target && (evt.target.id !== 'filters')) && (evt.target.offsetParent && (['filterMenu', 'filters'].indexOf(evt.target.offsetParent.id) === -1))) {
            searchStore.toggleFilter(false);
        }
        /****
         * Close drawer if click is outside of drawer
         ****/
        let clickInDrawer = false;
        let paths = evt ? evt.path : [];
        if (!paths) {
            paths = evt.composedPath();
        }
        for (let i = 0; i < paths.length; i++) {
            const path = paths[i];
            if (path.id === 'drawer') {
                clickInDrawer = true;
                break;
            }
        }
        if (!clickInDrawer) {
            const openingChat = $q.sessionStorage.getItem('openingChat');
            if (openingChat) {
                $q.sessionStorage.set('openingChat', false);
            }
            else if (styleType.value === 'desktop') {
                drawer.value = false;
            }
        }
        /*****
         * Scroll to filter to focus on input
         * if the click is on input
         *****/
        if (!$q.platform.is.capacitor &&
            evt.target?.nodeName.toLowerCase() === 'input') {
            evt.target?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center'
            });
        }
    }
    /****
     * Resend validation email
     *****/
    const { refreshPage } = RefreshPagePlugin();
    const showValidationDialog = ref(false);
    const { subject: resend$ } = rxInitSubjectCallback();
    const { axiosCall } = axiosCallerPlugin();
    useObservable(resend$.pipe(switchMap(() => {
        return of('').pipe(switchMap(() => {
            return axiosCall({
                type: 'create',
                state: 'validation',
                message: 'Validation Email Sent',
                alert: true,
                loading: true,
                urlType: 'common',
                url: 'resend_validation'
            });
        }));
    }), tap(({ success, error }) => {
        if (success)
            return;
        if (!error)
            return;
        const errorResponse = error.reason;
        loggingStore.errorHandler(error);
        if (!error)
            return;
        // if user is validated, refresh app
        if (errorResponse ===
            'User has been validated. Please refresh the browser') {
            refreshPage();
            centralStore.setRequeryInit();
        }
        loggingStore.errorHandler(error);
    }), filter((v) => v.success), tap(() => {
        showValidationDialog.value = true;
    })));
    return {
        drawer,
        pagePaddingClass,
        showHeader,
        user,
        checkPageClick,
        subscriptionSticky,
        updateSubscriber,
        showValidationDialog,
        resend$,
    };
};
export { LayoutPlugin };
